import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";


/*
|--------------------------------------------------------------------------
| Configuration
|--------------------------------------------------------------------------
*/

const MAX_IMAGE_WIDTH = 2000;
const MAX_IMAGE_HEIGHT = 2000;
const IMAGE_QUALITY = 0.82;

const MEDIA_TYPES = {
    images: {
        label: "Main Images",
        accept: "image/*"
    },

    videos: {
        label: "Videos",
        accept: "video/*"
    },

    gallery360: {
        label: "360° Images",
        accept: "image/*"
    },

    documents: {
        label: "Documents",
        accept: ".pdf,.doc,.docx,.xls,.xlsx,.txt"
    }
};


/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function createPreview(file) {

    if (!(file instanceof File)) {
        return null;
    }

    return URL.createObjectURL(file);

}


function getFileType(file) {

    if (!file) {
        return "images";
    }

    const type = file.type || "";

    if (type.startsWith("video/")) {
        return "videos";
    }

    if (type.startsWith("image/")) {
        return "images";
    }

    return "documents";

}


function isVideoItem(item) {

    if (!item) {
        return false;
    }

    if (item.mediaType === "videos") {
        return true;
    }

    if (item.type && item.type.startsWith("video/")) {
        return true;
    }

    if (
        typeof item.url === "string" &&
        /\.(mp4|mov|webm|ogg|m4v)$/i.test(item.url)
    ) {
        return true;
    }

    return false;

}


function isImageItem(item) {

    if (!item) {
        return false;
    }

    if (
        item.mediaType === "images" ||
        item.mediaType === "gallery360"
    ) {
        return true;
    }

    if (item.type && item.type.startsWith("image/")) {
        return true;
    }

    if (
        typeof item.url === "string" &&
        /\.(jpg|jpeg|png|webp|gif|avif|svg)$/i.test(item.url)
    ) {
        return true;
    }

    return false;

}


/*
|--------------------------------------------------------------------------
| Image Compression
|--------------------------------------------------------------------------
*/

async function compressImage(file) {

    if (!(file instanceof File)) {
        return file;
    }

    if (!file.type.startsWith("image/")) {
        return file;
    }

    /*
     * SVG/GIF are deliberately left untouched.
     */

    if (
        file.type === "image/svg+xml" ||
        file.type === "image/gif"
    ) {
        return file;
    }

    return new Promise((resolve) => {

        const image = new Image();

        const objectUrl = URL.createObjectURL(file);

        image.onload = () => {

            URL.revokeObjectURL(objectUrl);

            let width = image.width;
            let height = image.height;

            const scale = Math.min(
                1,
                MAX_IMAGE_WIDTH / width,
                MAX_IMAGE_HEIGHT / height
            );

            width = Math.round(width * scale);
            height = Math.round(height * scale);

            const canvas = document.createElement("canvas");

            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext("2d");

            if (!context) {

                resolve(file);

                return;

            }

            context.drawImage(
                image,
                0,
                0,
                width,
                height
            );

            canvas.toBlob(
                (blob) => {

                    if (!blob) {

                        resolve(file);

                        return;

                    }

                    const compressedFile = new File(
                        [blob],
                        file.name,
                        {
                            type: "image/jpeg",
                            lastModified: Date.now()
                        }
                    );

                    resolve(compressedFile);

                },
                "image/jpeg",
                IMAGE_QUALITY
            );

        };

        image.onerror = () => {

            URL.revokeObjectURL(objectUrl);

            resolve(file);

        };

        image.src = objectUrl;

    });

}


/*
|--------------------------------------------------------------------------
| Video Thumbnail
|--------------------------------------------------------------------------
*/

async function generateVideoThumbnail(file) {

    if (!(file instanceof File)) {
        return null;
    }

    if (!file.type.startsWith("video/")) {
        return null;
    }

    return new Promise((resolve) => {

        const video = document.createElement("video");

        const objectUrl = URL.createObjectURL(file);

        video.src = objectUrl;

        video.muted = true;

        video.playsInline = true;

        video.preload = "metadata";

        video.onloadeddata = () => {

            try {

                video.currentTime = Math.min(
                    1,
                    video.duration || 1
                );

            }

            catch {

                URL.revokeObjectURL(objectUrl);

                resolve(null);

            }

        };

        video.onseeked = () => {

            try {

                const canvas =
                    document.createElement("canvas");

                canvas.width =
                    video.videoWidth || 640;

                canvas.height =
                    video.videoHeight || 360;

                const context =
                    canvas.getContext("2d");

                context.drawImage(
                    video,
                    0,
                    0,
                    canvas.width,
                    canvas.height
                );

                canvas.toBlob(
                    (blob) => {

                        URL.revokeObjectURL(objectUrl);

                        if (!blob) {

                            resolve(null);

                            return;

                        }

                        resolve(
                            URL.createObjectURL(blob)
                        );

                    },
                    "image/jpeg",
                    0.8
                );

            }

            catch {

                URL.revokeObjectURL(objectUrl);

                resolve(null);

            }

        };

        video.onerror = () => {

            URL.revokeObjectURL(objectUrl);

            resolve(null);

        };

    });

}


/*
|--------------------------------------------------------------------------
| Component
|--------------------------------------------------------------------------
*/

export default function ProductMedia({

    product,

    setProduct,

    /*
     * Optional callbacks for the future PocketBase
     * upload layer.
     */

    onUploadProgress,

    onMediaChange

}) {

    const [activeGallery, setActiveGallery] =
        useState("images");

    const [draggedIndex, setDraggedIndex] =
        useState(null);

    const [dragOverIndex, setDragOverIndex] =
        useState(null);

    const [uploading, setUploading] =
        useState(false);

    const [uploadProgress, setUploadProgress] =
        useState(0);

    const [aspectRatio, setAspectRatio] =
        useState("original");

    const fileInputRef =
        useRef(null);


    /*
    |--------------------------------------------------------------------------
    | Normalize product media
    |--------------------------------------------------------------------------
    */

    const media = useMemo(() => {

        return Array.isArray(product?.media)
            ? product.media
            : [];

    }, [product?.media]);


    /*
    |--------------------------------------------------------------------------
    | Notify parent
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (typeof onMediaChange === "function") {

            onMediaChange(media);

        }

    }, [
        media,
        onMediaChange
    ]);


    /*
    |--------------------------------------------------------------------------
    | Cleanup object URLs
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        return () => {

            media.forEach(item => {

                if (
                    item?.preview &&
                    item.preview.startsWith("blob:")
                ) {

                    URL.revokeObjectURL(
                        item.preview
                    );

                }

                if (
                    item?.videoThumbnail &&
                    item.videoThumbnail.startsWith("blob:")
                ) {

                    URL.revokeObjectURL(
                        item.videoThumbnail
                    );

                }

            });

        };

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Update Product Media
    |--------------------------------------------------------------------------
    */

    const updateMedia = (newMedia) => {

        setProduct(prev => ({

            ...prev,

            media: newMedia

        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Process Files
    |--------------------------------------------------------------------------
    */

    const processFiles = async (files) => {

        if (!files || !files.length) {
            return;
        }

        setUploading(true);

        setUploadProgress(0);

        const incomingFiles =
            Array.from(files);

        const processed = [];

        for (
            let index = 0;
            index < incomingFiles.length;
            index++
        ) {

            const originalFile =
                incomingFiles[index];

            const type =
                getFileType(originalFile);

            let finalFile =
                originalFile;

            /*
             * Compress images.
             */

            if (
                type === "images" ||
                type === "gallery360"
            ) {

                finalFile =
                    await compressImage(
                        originalFile
                    );

            }

            const preview =
                createPreview(finalFile);

            let videoThumbnail = null;

            /*
             * Generate video thumbnail.
             */

            if (type === "videos") {

                videoThumbnail =
                    await generateVideoThumbnail(
                        finalFile
                    );

            }

            processed.push({

                id:
                    `new-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2)}`,

                file:
                    finalFile,

                originalFile,

                name:
                    finalFile.name,

                type:
                    finalFile.type,

                size:
                    finalFile.size,

                mediaType:
                    type,

                preview,

                videoThumbnail,

                existing:
                    false,

                uploadStatus:
                    "pending",

                uploadProgress:
                    0,

                aspectRatio

            });

            const progress =
                Math.round(
                    ((index + 1) /
                        incomingFiles.length) *
                        100
                );

            setUploadProgress(progress);

            if (
                typeof onUploadProgress ===
                "function"
            ) {

                onUploadProgress(
                    progress
                );

            }

        }

        /*
         * If no media exists, first image becomes cover.
         */

        const previousMedia =
            media;

        const newMedia = [
            ...previousMedia,
            ...processed
        ];

        let coverIndex =
            product?.coverIndex;

        if (
            typeof coverIndex !== "number" &&
            newMedia.length > 0
        ) {

            const firstImage =
                newMedia.findIndex(
                    item =>
                        isImageItem(item)
                );

            coverIndex =
                firstImage >= 0
                    ? firstImage
                    : 0;

        }

        setProduct(prev => ({

            ...prev,

            media: newMedia,

            coverIndex

        }));

        setUploading(false);

        setUploadProgress(100);

    };


    /*
    |--------------------------------------------------------------------------
    | File Picker
    |--------------------------------------------------------------------------
    */

    const handleFiles = (event) => {

        processFiles(
            event.target.files
        );

        /*
         * Allows selecting the same file again.
         */

        event.target.value = "";

    };


    /*
    |--------------------------------------------------------------------------
    | Drag & Drop Upload
    |--------------------------------------------------------------------------
    */

    const handleDrop = (event) => {

        event.preventDefault();

        event.stopPropagation();

        const files =
            event.dataTransfer.files;

        processFiles(files);

    };


    const handleDragOver = (event) => {

        event.preventDefault();

        event.stopPropagation();

    };


    /*
    |--------------------------------------------------------------------------
    | Remove Media
    |--------------------------------------------------------------------------
    */

    const removeMedia = (index) => {

        const item =
            media[index];

        if (
            item?.preview &&
            item.preview.startsWith("blob:")
        ) {

            URL.revokeObjectURL(
                item.preview
            );

        }

        if (
            item?.videoThumbnail &&
            item.videoThumbnail.startsWith("blob:")
        ) {

            URL.revokeObjectURL(
                item.videoThumbnail
            );

        }

        const updated =
            media.filter(
                (_, i) =>
                    i !== index
            );

        let coverIndex =
            product?.coverIndex;

        if (
            coverIndex === index
        ) {

            coverIndex =
                updated.length > 0
                    ? 0
                    : null;

        }
        else if (
            typeof coverIndex === "number" &&
            coverIndex > index
        ) {

            coverIndex =
                coverIndex - 1;

        }

        updateMedia(updated);

        setProduct(prev => ({

            ...prev,

            coverIndex

        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Set Cover
    |--------------------------------------------------------------------------
    */

    const setCover = (index) => {

        setProduct(prev => ({

            ...prev,

            coverIndex:
                index

        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Reorder
    |--------------------------------------------------------------------------
    */

    const handleItemDragStart = (
        index
    ) => {

        setDraggedIndex(index);

    };


    const handleItemDragOver = (
        event,
        index
    ) => {

        event.preventDefault();

        setDragOverIndex(index);

    };


    const handleItemDrop = (
        event,
        targetIndex
    ) => {

        event.preventDefault();

        if (
            draggedIndex === null ||
            draggedIndex === targetIndex
        ) {

            setDraggedIndex(null);

            setDragOverIndex(null);

            return;

        }

        const updated =
            [...media];

        const moved =
            updated.splice(
                draggedIndex,
                1
            )[0];

        updated.splice(
            targetIndex,
            0,
            moved
        );

        /*
         * Keep cover attached to the same media item.
         */

        let newCoverIndex =
            product?.coverIndex;

        if (
            typeof newCoverIndex === "number"
        ) {

            if (
                newCoverIndex ===
                draggedIndex
            ) {

                newCoverIndex =
                    targetIndex;

            }
            else if (
                draggedIndex <
                newCoverIndex &&
                targetIndex >=
                newCoverIndex
            ) {

                newCoverIndex--;

            }
            else if (
                draggedIndex >
                newCoverIndex &&
                targetIndex <=
                newCoverIndex
            ) {

                newCoverIndex++;

            }

        }

        setProduct(prev => ({

            ...prev,

            media: updated,

            coverIndex:
                newCoverIndex

        }));

        setDraggedIndex(null);

        setDragOverIndex(null);

    };


    /*
    |--------------------------------------------------------------------------
    | Gallery Filtering
    |--------------------------------------------------------------------------
    */

    const galleryMedia =
        media.filter(item => {

            if (
                activeGallery ===
                "images"
            ) {

                return (
                    item.mediaType ===
                        "images" ||
                    (
                        !item.mediaType &&
                        isImageItem(item)
                    )
                );

            }

            if (
                activeGallery ===
                "videos"
            ) {

                return (
                    item.mediaType ===
                    "videos" ||
                    isVideoItem(item)
                );

            }

            if (
                activeGallery ===
                "gallery360"
            ) {

                return (
                    item.mediaType ===
                    "gallery360"
                );

            }

            if (
                activeGallery ===
                "documents"
            ) {

                return (
                    item.mediaType ===
                    "documents"
                );

            }

            return true;

        });


    /*
    |--------------------------------------------------------------------------
    | Render Media
    |--------------------------------------------------------------------------
    */

    const getPreview =
        (item) => {

            if (item?.preview) {
                return item.preview;
            }

            if (item?.url) {
                return item.url;
            }

            return null;

        };


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 24
            }}
        >

            {/* Header */}

            <div>

                <h2
                    style={{
                        margin: 0
                    }}
                >
                    Product Media
                </h2>

                <p
                    style={{
                        color: "#666",
                        marginTop: 8,
                        lineHeight: 1.6
                    }}
                >
                    Manage your product images,
                    videos, 360° media and documents.
                    Drag media to reorder it and select
                    a cover image for the Marketplace.
                </p>

            </div>


            {/* Gallery Tabs */}

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10
                }}
            >

                {Object.entries(
                    MEDIA_TYPES
                ).map(([key, value]) => (

                    <button
                        key={key}
                        type="button"
                        onClick={() =>
                            setActiveGallery(key)
                        }
                        style={{
                            padding:
                                "10px 16px",
                            borderRadius: 10,
                            border:
                                activeGallery === key
                                    ? "1px solid rgb(255,0,204)"
                                    : "1px solid #ddd",
                            background:
                                activeGallery === key
                                    ? "rgba(255,0,204,.08)"
                                    : "#fff",
                            color:
                                activeGallery === key
                                    ? "rgb(255,0,204)"
                                    : "#333",
                            fontWeight: 600,
                            cursor: "pointer"
                        }}
                    >
                        {value.label}
                    </button>

                ))}

            </div>


            {/* Upload Area */}

            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                style={{
                    background: "#fff",
                    border:
                        "2px dashed #d4d4d8",
                    borderRadius: 18,
                    padding: 40,
                    textAlign: "center",
                    transition: ".2s"
                }}
            >

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={
                        MEDIA_TYPES[
                            activeGallery
                        ].accept
                    }
                    onChange={
                        handleFiles
                    }
                    style={{
                        display: "none"
                    }}
                />

                <div
                    style={{
                        fontSize: 40
                    }}
                >
                    ＋
                </div>

                <h3
                    style={{
                        marginBottom: 8
                    }}
                >
                    Drag & Drop Media
                </h3>

                <p
                    style={{
                        color: "#777"
                    }}
                >
                    or choose files from
                    your computer
                </p>

                <button
                    type="button"
                    onClick={() =>
                        fileInputRef.current?.click()
                    }
                    style={{
                        marginTop: 10,
                        padding:
                            "12px 20px",
                        border: "none",
                        borderRadius: 10,
                        background:
                            "rgb(255,0,204)",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: 700
                    }}
                >
                    Choose Files
                </button>

            </div>


            {/* Upload Progress */}

            {uploading && (

                <div
                    style={{
                        background: "#fff",
                        borderRadius: 14,
                        padding: 20,
                        border:
                            "1px solid #eee"
                    }}
                >

                    <div
                        style={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            marginBottom: 10,
                            fontWeight: 600
                        }}
                    >

                        <span>
                            Processing media...
                        </span>

                        <span>
                            {uploadProgress}%
                        </span>

                    </div>

                    <div
                        style={{
                            height: 8,
                            background: "#eee",
                            borderRadius: 10,
                            overflow: "hidden"
                        }}
                    >

                        <div
                            style={{
                                width:
                                    `${uploadProgress}%`,
                                height: "100%",
                                background:
                                    "rgb(255,0,204)",
                                transition:
                                    ".2s"
                            }}
                        />

                    </div>

                </div>

            )}


            {/* Aspect Ratio */}

            {(activeGallery === "images" ||
                activeGallery === "gallery360") && (

                <div
                    style={{
                        background: "#fff",
                        borderRadius: 14,
                        padding: 20,
                        border:
                            "1px solid #eee"
                    }}
                >

                    <strong>
                        Image Aspect Ratio
                    </strong>

                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 10,
                            marginTop: 12
                        }}
                    >

                        {[
                            "original",
                            "1:1",
                            "4:5",
                            "16:9"
                        ].map(ratio => (

                            <button
                                key={ratio}
                                type="button"
                                onClick={() =>
                                    setAspectRatio(
                                        ratio
                                    )
                                }
                                style={{
                                    padding:
                                        "8px 14px",
                                    borderRadius: 8,
                                    border:
                                        aspectRatio === ratio
                                            ? "1px solid rgb(255,0,204)"
                                            : "1px solid #ddd",
                                    background:
                                        aspectRatio === ratio
                                            ? "rgba(255,0,204,.08)"
                                            : "#fff",
                                    cursor:
                                        "pointer"
                                }}
                            >
                                {ratio ===
                                    "original"
                                    ? "Original"
                                    : ratio}
                            </button>

                        ))}

                    </div>

                    <p
                        style={{
                            marginBottom: 0,
                            color: "#777",
                            fontSize: 13
                        }}
                    >
                        The selected ratio is stored
                        with newly added media and can
                        be used by the future cropper.
                    </p>

                </div>

            )}


            {/* Media Grid */}

            {galleryMedia.length > 0 ? (

                <div>

                    <h3>
                        {MEDIA_TYPES[
                            activeGallery
                        ].label}
                    </h3>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fill,minmax(190px,1fr))",
                            gap: 18
                        }}
                    >

                        {galleryMedia.map(
                            (item) => {

                                const actualIndex =
                                    media.indexOf(
                                        item
                                    );

                                const preview =
                                    getPreview(
                                        item
                                    );

                                const isCover =
                                    product?.coverIndex ===
                                    actualIndex;

                                const video =
                                    isVideoItem(
                                        item
                                    );

                                const image =
                                    isImageItem(
                                        item
                                    );

                                return (

                                    <div
                                        key={
                                            item.id ||
                                            actualIndex
                                        }
                                        draggable
                                        onDragStart={() =>
                                            handleItemDragStart(
                                                actualIndex
                                            )
                                        }
                                        onDragOver={event =>
                                            handleItemDragOver(
                                                event,
                                                actualIndex
                                            )
                                        }
                                        onDrop={event =>
                                            handleItemDrop(
                                                event,
                                                actualIndex
                                            )
                                        }
                                        style={{
                                            background:
                                                "#fff",
                                            border:
                                                isCover
                                                    ? "3px solid rgb(255,0,204)"
                                                    : dragOverIndex ===
                                                      actualIndex
                                                        ? "2px dashed rgb(255,0,204)"
                                                        : "1px solid #ddd",
                                            borderRadius:
                                                16,
                                            overflow:
                                                "hidden",
                                            cursor:
                                                "grab"
                                        }}
                                    >

                                        {/* Preview */}

                                        <div
                                            style={{
                                                height: 180,
                                                background:
                                                    "#f1f1f1",
                                                position:
                                                    "relative"
                                            }}
                                        >

                                            {video &&
                                            (
                                                item.videoThumbnail ||
                                                preview
                                            ) ? (

                                                <div
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        position: "relative"
                                                    }}
                                                >

                                                    <video
                                                        src={
                                                            preview
                                                        }
                                                        controls
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit:
                                                                "cover"
                                                        }}
                                                    />

                                                    {item.videoThumbnail && (

                                                        <div
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                top: 10,
                                                                left: 10,
                                                                background:
                                                                    "rgba(0,0,0,.65)",
                                                                color:
                                                                    "#fff",
                                                                padding:
                                                                    "4px 8px",
                                                                borderRadius:
                                                                    6,
                                                                fontSize:
                                                                    11
                                                            }}
                                                        >
                                                            Video
                                                        </div>

                                                    )}

                                                </div>

                                            ) : image &&
                                              preview ? (

                                                <img
                                                    src={
                                                        preview
                                                    }
                                                    alt={
                                                        item.name ||
                                                        "Product media"
                                                    }
                                                    style={{
                                                        width:
                                                            "100%",
                                                        height:
                                                            "100%",
                                                        objectFit:
                                                            "cover"
                                                    }}
                                                />

                                            ) : (

                                                <div
                                                    style={{
                                                        height:
                                                            "100%",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        padding:
                                                            20,
                                                        textAlign:
                                                            "center",
                                                        color:
                                                            "#666"
                                                    }}
                                                >
                                                    {item.name ||
                                                        "Document"}
                                                </div>

                                            )}

                                            {/* Cover */}

                                            {isCover && (

                                                <div
                                                    style={{
                                                        position:
                                                            "absolute",
                                                        top: 10,
                                                        right: 10,
                                                        background:
                                                            "rgb(255,0,204)",
                                                        color:
                                                            "#fff",
                                                        padding:
                                                            "5px 9px",
                                                        borderRadius:
                                                            7,
                                                        fontSize:
                                                            11,
                                                        fontWeight:
                                                            700
                                                    }}
                                                >
                                                    COVER
                                                </div>

                                            )}

                                        </div>


                                        {/* Details */}

                                        <div
                                            style={{
                                                padding:
                                                    14,
                                                display:
                                                    "flex",
                                                flexDirection:
                                                    "column",
                                                gap: 10
                                            }}
                                        >

                                            <div
                                                style={{
                                                    fontWeight:
                                                        600,
                                                    overflow:
                                                        "hidden",
                                                    textOverflow:
                                                        "ellipsis",
                                                    whiteSpace:
                                                        "nowrap"
                                                }}
                                            >
                                                {item.name ||
                                                    "Untitled media"}
                                            </div>

                                            {item.size && (

                                                <div
                                                    style={{
                                                        color:
                                                            "#777",
                                                        fontSize:
                                                            12
                                                    }}
                                                >
                                                    {(
                                                        item.size /
                                                        1024 /
                                                        1024
                                                    ).toFixed(
                                                        2
                                                    )}{" "}
                                                    MB
                                                </div>

                                            )}

                                            <div
                                                style={{
                                                    display:
                                                        "flex",
                                                    gap: 8
                                                }}
                                            >

                                                {image && (

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setCover(
                                                                actualIndex
                                                            )
                                                        }
                                                        style={{
                                                            flex: 1,
                                                            padding:
                                                                9,
                                                            border:
                                                                "none",
                                                            borderRadius:
                                                                8,
                                                            background:
                                                                isCover
                                                                    ? "rgb(255,0,204)"
                                                                    : "#f1f1f1",
                                                            color:
                                                                isCover
                                                                    ? "#fff"
                                                                    : "#222",
                                                            cursor:
                                                                "pointer",
                                                            fontWeight:
                                                                600
                                                        }}
                                                    >
                                                        {isCover
                                                            ? "Cover"
                                                            : "Set Cover"}
                                                    </button>

                                                )}

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeMedia(
                                                            actualIndex
                                                        )
                                                    }
                                                    style={{
                                                        padding:
                                                            9,
                                                        border:
                                                            "none",
                                                        borderRadius:
                                                            8,
                                                        background:
                                                            "#ef4444",
                                                        color:
                                                            "#fff",
                                                        cursor:
                                                            "pointer"
                                                    }}
                                                >
                                                    Remove
                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                </div>

            ) : (

                <div
                    style={{
                        background: "#fff",
                        borderRadius: 14,
                        padding: 40,
                        textAlign: "center",
                        border:
                            "1px solid #eee",
                        color: "#777"
                    }}
                >

                    No media in this gallery yet.

                </div>

            )}

        </div>

    );

}